import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLeadScores, getColdLeads, selectLeadScores, selectColdLeads } from "../../store/slices/aiSlice";
import { Flame, Thermometer, Snowflake, Skull, AlertTriangle, Clock, RefreshCw, Loader2 } from "lucide-react";
import { ListSkeleton } from "../UI/Skeleton";
import AIPanel, { AI_LABEL } from "./AIPanel";

const labelConfig = {
  hot:  { icon: Flame,       color: "text-red-600",    bg: "bg-red-50",    border: "border-red-200",    bar: "bg-red-500" },
  warm: { icon: Thermometer, color: "text-amber-600",  bg: "bg-amber-50",  border: "border-amber-200",  bar: "bg-amber-500" },
  cold: { icon: Snowflake,   color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-200", bar: "bg-purple-500" },
  dead: { icon: Skull,       color: "text-gray-500",   bg: "bg-gray-100",  border: "border-gray-200",   bar: "bg-gray-400" },
};

function Badge({ label }) {
  const cfg = labelConfig[label] || labelConfig.cold;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold capitalize ${cfg.bg} ${cfg.border} ${cfg.color}`}>
      <Icon size={11} /> {label}
    </span>
  );
}

function ScoreBar({ score }) {
  const label = score >= 75 ? "hot" : score >= 50 ? "warm" : score >= 25 ? "cold" : "dead";
  const cfg = labelConfig[label];
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${cfg.bar}`} style={{ width: `${score}%` }} />
      </div>
      <span className="w-6 text-right text-xs font-bold text-gray-900 tabular-nums">{score}</span>
    </div>
  );
}

const initialsOf = (name) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join("");

export default function LeadScoreBoard() {
  const dispatch = useDispatch();
  const { data: leadData, loading } = useSelector(selectLeadScores);
  const { data: coldData } = useSelector(selectColdLeads);

  const refresh = useCallback(() => {
    dispatch(getLeadScores("pending,confirmed"));
    dispatch(getColdLeads(6));
  }, [dispatch]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 60000);
    return () => clearInterval(interval);
  }, [refresh]);

  const leads = leadData?.leads || [];
  const stats = leadData?.stats || {};
  const coldLeads = coldData?.leads || [];

  return (
    <AIPanel
      icon={Flame}
      title="Lead Score Board"
      description="Leads ranked by how likely they are to book. Refreshes every minute."
      action={
        <button
          onClick={refresh}
          disabled={loading}
          aria-label="Refresh leads"
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:border-purple-200 hover:bg-purple-50 transition-colors"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
        </button>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: "Hot", count: stats.hot || 0, cfg: labelConfig.hot },
          { label: "Warm", count: stats.warm || 0, cfg: labelConfig.warm },
          { label: "Cold", count: stats.cold || 0, cfg: labelConfig.cold },
          { label: "Dead", count: stats.dead || 0, cfg: labelConfig.dead },
        ].map(({ label, count, cfg }) => (
          <div key={label} className="flex items-center gap-3 rounded-xl border border-gray-200/70 bg-gray-50/60 p-3.5">
            <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${cfg.bg}`}>
              <cfg.icon size={16} className={cfg.color} />
            </div>
            <div>
              <p className="font-['Manrope'] text-xl font-extrabold text-gray-900 tabular-nums leading-none">{count}</p>
              <p className={`mt-1 ${AI_LABEL}`}>{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Lead list */}
      {leads.length === 0 && loading && <ListSkeleton rows={3} className="space-y-2 mb-5" />}
      {leads.length === 0 && !loading && (
        <div className="rounded-xl border border-dashed border-gray-300 px-4 py-10 text-center">
          <p className="text-sm font-semibold text-gray-900">No pending leads found</p>
          <p className="text-sm text-gray-500 mt-1">New enquiries will be scored and listed here.</p>
        </div>
      )}

      {leads.length > 0 && (
        <div className="space-y-2 mb-5 max-h-96 overflow-y-auto pr-1">
          {leads.map(({ booking, score, label, ai_summary }) => {
            const name = booking.user_id?.full_name || "Customer";
            return (
              <div key={booking._id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200/70 bg-white hover:border-purple-200 hover:shadow-md hover:shadow-purple-900/5 transition-all">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-pink-100 text-xs font-bold text-gray-700">
                  {initialsOf(name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
                    <Badge label={label} />
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {booking.venue_id?.name || "Venue"} &middot; {booking.function_type} &middot; {booking.number_of_guests} guests
                  </p>
                  {ai_summary && (
                    <p className="text-xs text-gray-600 mt-1 truncate">{ai_summary}</p>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="font-['Manrope'] text-sm font-bold text-gray-900 tabular-nums mb-1">
                    ₹{(booking.pricing_details?.total_amount || 0).toLocaleString("en-IN")}
                  </p>
                  <ScoreBar score={score} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Cold leads alert */}
      {coldLeads.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
          <h4 className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-3">
            <AlertTriangle size={16} className="text-amber-500" /> Cold Lead Alerts ({coldLeads.length})
          </h4>
          <div className="space-y-2">
            {coldLeads.slice(0, 5).map((lead) => (
              <div key={lead._id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-white border border-amber-100">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{lead.user_id?.full_name || "Customer"}</p>
                  <p className="text-xs text-gray-500 truncate">{lead.venue_id?.name || "Venue"}</p>
                </div>
                <span className="flex flex-shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                  <Clock size={12} /> {lead.hoursWithoutResponse}h ignored
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </AIPanel>
  );
}
